import { useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import {
  ShiftRateNegotiationRole,
  ShiftRateNegotiationStatus,
} from "@src/appV2/OpenShifts/RateNegotiation/types";
import { type BackgroundCheckMissingDataType } from "@src/appV2/OpenShifts/ShiftAction/types";
import { Button } from "@src/appV2/redesign/components/Button";
import { useRequiresBreakPolicyAcknowledgement } from "@src/appV2/redesign/Shift/Booking/useRequiresBreakPolicyAcknowledgement";
import { BreakConfirmationBottomSheet } from "@src/appV2/redesign/Shift/BreakConfirmationBottomSheet/BreakConfirmationBottomSheet";
import { generatePath, useHistory } from "react-router-dom";

import { TextButton } from "../../components/TextButton";
import { SHIFT_DISCOVERY_SHIFT_NEGOTIATION_MODAL_PATH } from "../../ShiftDiscovery/paths";
import { type NavigateToModalFunction } from "../../ShiftDiscovery/useShiftModalsDataContext";
import { createBackgroundCheckUrlFromMissingDataTypes } from "../createBackgroundCheckUrlFromMissingDataTypes";
import { type RateNegotiationToolLocationState } from "../types";

interface RateNegotiationActionProps {
  shiftId: string;
  workplaceId: string;
  rateNegotiationStatus?: ShiftRateNegotiationStatus;
  rateNegotiationLastOfferBy?: ShiftRateNegotiationRole;
  rateNegotiationLastOfferPayRate?: number;
  useNewRateNegotiationUi?: boolean;
  navigateToModal: NavigateToModalFunction;
  rateNegotiationId?: string;
  missingBackgroundCheckDataTypes: BackgroundCheckMissingDataType[];
}

export function RateNegotiationAction(props: RateNegotiationActionProps) {
  const {
    rateNegotiationStatus,
    rateNegotiationLastOfferBy,
    rateNegotiationLastOfferPayRate,
    shiftId,
    workplaceId,
    useNewRateNegotiationUi = false,
    rateNegotiationId,
    navigateToModal,
    missingBackgroundCheckDataTypes = [],
  } = props;

  const history = useHistory<RateNegotiationToolLocationState>();
  const breakPolicyModalState = useModalState();

  const { linkSource } = useSearchParams();

  const { requiresBreakPolicyAcknowledgement, breakPolicyNoteId, isLoading } =
    useRequiresBreakPolicyAcknowledgement({
      workplaceId,
    });

  function getNegotiationButtonText() {
    if (!isDefined(rateNegotiationStatus)) {
      return "Negotiate rate";
    }

    switch (rateNegotiationStatus) {
      case ShiftRateNegotiationStatus.OPEN: {
        if (
          rateNegotiationLastOfferBy === ShiftRateNegotiationRole.WORKPLACE &&
          isDefined(rateNegotiationLastOfferPayRate)
        ) {
          return `Reply to proposal: ${formatDollarsAsUsd(rateNegotiationLastOfferPayRate, {
            minimumFractionDigits: 0,
          })}/hr`;
        }

        return "View proposal";
      }

      case ShiftRateNegotiationStatus.EXPIRED: {
        return "Proposal expired";
      }

      case ShiftRateNegotiationStatus.REJECTED: {
        if (rateNegotiationLastOfferBy === ShiftRateNegotiationRole.WORKPLACE) {
          return "Proposal rejected";
        }

        return undefined;
      }

      default: {
        return undefined;
      }
    }
  }

  const label = getNegotiationButtonText();

  if (!isDefined(label) || isLoading) {
    return null;
  }

  function navigateToRateNegotiationTool() {
    const searchQuery = typeof linkSource === "string" ? `?linkSource=${linkSource}` : "";

    if (useNewRateNegotiationUi) {
      navigateToModal(SHIFT_DISCOVERY_SHIFT_NEGOTIATION_MODAL_PATH, {
        pathParams: { shiftId },
        searchParams: rateNegotiationId ? { rateNegotiationId } : {},
      });
    } else {
      history.push(
        `${generatePath(DeprecatedGlobalAppV1Paths.RATE_NEGOTIATION_TOOL, {
          shiftId,
        })}${searchQuery}`,
        {
          // We pass origin to make decide where to redirect after booking a shift from rate negotiation tool
          origin: "SHIFT_DETAILS_SHEET",
        }
      );
    }
  }

  const isDisabled =
    rateNegotiationStatus === ShiftRateNegotiationStatus.EXPIRED ||
    rateNegotiationStatus === ShiftRateNegotiationStatus.REJECTED;

  const onClick = () => {
    if (missingBackgroundCheckDataTypes.length > 0) {
      const url = createBackgroundCheckUrlFromMissingDataTypes({
        queryParams: new URLSearchParams(history.location.search),
        shiftId,
        missingBackgroundCheckDataTypes,
      });
      history.push(url);
      return;
    }

    if (requiresBreakPolicyAcknowledgement) {
      breakPolicyModalState.openModal();
    } else {
      navigateToRateNegotiationTool();
    }
  };

  return (
    <>
      {useNewRateNegotiationUi ? (
        <Button fullWidth size="large" variant="outlined" disabled={isDisabled} onClick={onClick}>
          {label}
        </Button>
      ) : (
        <TextButton sx={{ marginBottom: 3 }} onClick={onClick}>
          {label}
        </TextButton>
      )}
      {requiresBreakPolicyAcknowledgement && (
        <BreakConfirmationBottomSheet
          shiftId={shiftId}
          workplaceId={workplaceId}
          breakPolicyNoteId={breakPolicyNoteId!}
          modalState={breakPolicyModalState}
          onAcknowledge={async () => {
            breakPolicyModalState.closeModal();
            navigateToRateNegotiationTool();
          }}
        />
      )}
    </>
  );
}
